import * as Sentry from '@sentry/react';
import { ComponentDescription } from '@sapappgyver/appgyver-descriptors/lib/dolan/v4';
import { current, produce } from 'immer';
import migrateComponentPropertyDefinitions from './migrateComponentPropertyDefinitions';

const LEGACY_JSON_KEYS: Array<keyof ComponentDescription> = [
  'name',
  'args',
  'compatibility',
  'displayName',
  'icon',
  'is_module',
  'repeatable',
  'screenshots',
  'tags',
  'css_defaults',
  'visible_to_user',
  'events',
];

const migrateComponentDescription = produce<ComponentDescription>((draftDescription) => {
  const args = current(draftDescription.args);
  const migratedArgs = migrateComponentPropertyDefinitions(args);
  if (migratedArgs !== args) {
    draftDescription.args = migratedArgs;
  }
  draftDescription.events ||= [];

  if (draftDescription.reactnative_style_properties == null) {
    const fallbackStyleGroups: never[] = [];

    const componentStyleGroups: Record<string, string[]> = {
      AnimatedList: ['Layout', 'View', 'Text', 'Shadow', 'Transform'],
      button: ['Layout', 'View', 'Text', 'Shadow', 'Transform', 'Image'],
      picture_field: ['Layout', 'View', 'Shadow', 'Transform', 'Image'],
      text: ['Layout', 'View', 'Text', 'Shadow', 'Transform'],
      title: ['Layout', 'View', 'Text', 'Shadow', 'Transform'],
      card: ['Layout', 'View', 'Text', 'Shadow', 'Transform', 'Image'],
      toggle: ['Layout', 'View', 'Text', 'Shadow', 'Transform', 'Image'],
      panel: ['Layout', 'View', 'Text', 'Shadow', 'Transform'],
      text_field: ['Layout', 'View', 'Text', 'Shadow', 'Transform'],
      text_area: ['Layout', 'View', 'Text', 'Shadow'],
      mobile_tabbed_web_sidepanel_layout: ['Layout', 'View'],
      responsive_column_layout: ['Layout', 'View', 'Shadow', 'Transform'],
      vertical_box_layout: ['Layout', 'View', 'Shadow', 'Transform'],
      fixed_column_layout: ['Layout', 'View', 'Shadow', 'Transform'],
      list_divider: ['Layout', 'View', 'Text', 'Shadow', 'Transform'],
      checkbox: ['Layout', 'View', 'Text', 'Shadow', 'Transform', 'Image'],
      card_with_image_on_left_two_text_rows: ['Layout', 'View', 'Text', 'Shadow', 'Transform', 'Image'],
      list_item: ['Layout', 'View', 'Text', 'Shadow', 'Transform'],
      list_item_text_with_label: ['Layout', 'View', 'Text', 'Shadow', 'Transform'],
      text_field_with_stacked_label: ['Layout', 'View', 'Text', 'Shadow', 'Transform'],
      html_view: ['Layout', 'View', 'Text'],
    };
    const styleGroupName: string | undefined = draftDescription.legacy_json?.name;
    const groups =
      styleGroupName != null && componentStyleGroups[styleGroupName] != null
        ? componentStyleGroups[styleGroupName]
        : fallbackStyleGroups;
    draftDescription.reactnative_style_properties = {
      default_values: {},
      visibilities: {},
      unset_fallback_values: {},
      property_groups: Array.from(groups).map((groupName) => ({
        class_selector: null,
        name: groupName,
      })),
    };
    Sentry.captureMessage('DOLAN component was migrated to contain a missing `reactnative_style_properties` property', {
      level: 'warning',
      extra: {
        componentName: draftDescription.name,
      },
    });
  }

  // Attach new container property to all loaded components
  if (draftDescription.container == null) {
    draftDescription.container = false;
    Sentry.captureMessage('DOLAN component was migrated to contain a missing `container` property', {
      level: 'warning',
      extra: {
        componentName: draftDescription.name,
      },
    });
  }
  // Flatten some very old `legacy_json` object (originally done by DolanRuntime)
  const { legacy_json } = draftDescription;
  if (legacy_json) {
    const legacyJsonKeys = LEGACY_JSON_KEYS.filter((key) => legacy_json[key] != null);
    if (legacyJsonKeys.length > 0) {
      legacyJsonKeys.forEach((key) => {
        if (draftDescription[key] == null) {
          // set value from legacy_json to component definition (cannot type properly so casting)
          (draftDescription as any)[key] = legacy_json[key];
        }
      });
      delete draftDescription.legacy_json;
      Sentry.captureMessage('DOLAN component was migrated from `legacy_json` structure', {
        level: 'warning',
        extra: {
          componentName: draftDescription.name,
          legacyJsonKeys,
        },
      });
    }
  }
});

export default migrateComponentDescription;
